import { prepareFinagoAuditData } from './finagoAuditData';
import { getCurrentLang } from '@engine/index';

export const SOURCES = {
    '24so': { label: 'Finago', list: '/list-24so', data: '/generate-data', transactions: '/get-transactions', idField: 'identityId' },
    tripletex: { label: 'Tripletex', list: '/list-tripletex', data: '/generate-data-tripletex', transactions: '/get-transactions-tripletex', idField: 'companyId' },
    unimicro: { label: 'Uni Micro', list: '/list-unimicro', data: '/generate-data-unimicro', transactions: '/get-transactions-unimicro', idField: 'companyKey' },
};
export function sourceConfig(source) {
    if (!Object.hasOwn(SOURCES, source)) throw new Error('Unknown accounting source');
    return SOURCES[source];
}
export const visibleSources = role => role === 'admin' ? ['24so','tripletex','unimicro'] : ['24so','tripletex'];
export function normalizeSavedSource(saved, role) {
    if (!Object.hasOwn(SOURCES, saved)) return '24so';
    return saved === 'unimicro' && role != null && role !== 'admin' ? '24so' : saved;
}
export function sourceMatchesSystem(source, systemName) {
    const name = String(systemName || '').trim().toLowerCase();
    if (!name) return true;
    if (source === '24so') return /finago|24seven|24so|24 seven/.test(name);
    if (source === 'tripletex') return name.includes('tripletex');
    if (source === 'unimicro') return /uni micro|unimicro/.test(name);
    return false;
}
export function systemLabel(system) {
    if (!system || ['24sevenoffice','24seven'].includes(String(system).toLowerCase())) return 'Finago';
    if (['unimicro','uni micro'].includes(String(system).toLowerCase())) return 'Uni Micro';
    return system;
}
export const getListEndpoint = source => sourceConfig(source).list;
export const getDataEndpoint = source => sourceConfig(source).data;
export const getAuditDataEndpoint = source => source === '24so' ? '/audit-data' : getDataEndpoint(source);
export const getTransactionsEndpoint = source => sourceConfig(source).transactions;
export const getClientId = (client, source) => source === 'tripletex'
    ? `tripletex:${String(client.companyId || client.identityId || '').replace(/^tripletex:/,'')}`
    : source === 'unimicro' ? client.companyKey || client.identityId : client.identityId;
export function normalizeClients(rows, source) {
    sourceConfig(source);
    return rows.filter(c => !c.isDisabled).map(c => ({...c, source, system: sourceConfig(source).label,
        identityId: getClientId(c, source), name: c.name || c.clientName || c.companyName || '',
        organizationNumber: String(c.organizationNumber || c.orgNumber || c.orgNr || ''),
    }));
}
export function auditRequest(client, source, period) {
    const cfg = sourceConfig(source);
    const id = source === 'tripletex' ? getClientId(client,source).replace(/^tripletex:/,'') : getClientId(client,source);
    return { [cfg.idField]: id, companyName: client.name || client.clientName || client.companyName || '',
        year: Number(period.year), monthFrom: Number(period.monthFrom), month: Number(period.monthTo), force: true };
}
export function prepareApiAuditData(data, lines, expected, source) {
    const config = sourceConfig(source);
    if (source === 'tripletex' || source === 'unimicro') {
        if (lines?.complete !== true || !String(expected?.[config.idField] ?? '').trim()
            || [data,lines].some(x => x?.source !== source
                || String(x[config.idField]) !== String(expected[config.idField]))) {
            const e = new Error(getCurrentLang() === 'pl'
                ? `${config.label}: nie potwierdzono firmy lub kompletności księgi. Audyt został zatrzymany.`
                : `${config.label}: firma eller fullstendig hovedbok er ikke bekreftet. Kontrollen er stoppet.`);
            e.code = 'AUDIT_DATA_INVALID'; throw e;
        }
    }
    // API connectors share the signed, booked-amount contract. CSV formats do not.
    // An explicitly partial comparative balance must never become a 31 December balance.
    const prev = data?.previousYear;
    const input = prev && Object.hasOwn(prev,'saldoEndMonth') && Number(prev.saldoEndMonth) !== 12
        ? {...data,previousYear:{...prev,saldo:[]}} : data;
    try { return prepareFinagoAuditData(input, lines, expected); }
    catch (e) { e.message = e.message.replaceAll('Finago', config.label); throw e; }
}

// Fill a missing ERP org number only from a unique exact-name directory match.
// Existing ERP identifiers and conflicting/malformed mappings are never replaced.
export function resolveAuditOrg(client, directory = [], catalog = []) {
 const clean=value=>String(value||'').replace(/\s/g,'');
 const direct=clean(client?.organizationNumber||client?.orgNumber||client?.orgNr);
 if(direct)return /^\d{9}$/.test(direct)?direct:'';
 const key=value=>String(value||'').normalize('NFC').trim().replace(/\s+/g,' ').toLowerCase();
 const expected=key(client?.name||client?.clientName||client?.companyName);
 if(!expected)return '';
 const identity=row=>String(row?.identityId||row?.companyId||row?.companyKey||'');
 const peers=catalog.filter(row=>!row.isDisabled && row.source===client.source && key(row.name||row.clientName||row.companyName)===expected);
 if(!identity(client) || !peers.length || peers.some(row=>!identity(row)) || new Set(peers.map(identity)).size!==1 || identity(peers[0])!==identity(client))return '';
 const matches=directory.filter(row=>key(row.name)===expected);
 if(!matches.length || matches.some(row=>!/^\d{9}$/.test(clean(row.orgNr)) || /inaktiv|inactive/i.test(String(row.active||''))))return '';
 const ids=[...new Set(matches.map(row=>clean(row.orgNr)))];
 return ids.length===1?ids[0]:'';
}
